<template>
    <div>
        <!-- Modal dialog content (email signature form) -->
        <modal :id="modalId" :title="title">
            <div v-if="error">
                <div class="alert alert-danger">
                    {{ error }}
                </div>
            </div>

            <!-- Email -->
            <div class="form-group row">
              <label for="email" class="col-sm-2 col-form-label">Email</label>
              <div class="col-sm-10">
                <input type="email" class="form-control" v-model="fields.email" required>
                <div v-if="errors && errors.email" class="text-danger">{{ errors.email[0] }}</div>
              </div>
            </div>

            <!-- Signature -->
            <div class="form-group row">
              <label for="signature" class="col-sm-2 col-form-label">Signature</label>
              <div class="col-sm-10">
                <div>
                  <QuillEditor v-model:content="fields.signature" toolbar="full" content-type="html" theme="snow" />
                </div>
                <div v-if="errors && errors.signature" class="text-danger">{{ errors.signature[0] }}</div>
              </div>
            </div>

            <template #footer>
                <button type="button" class="btn btn-default" @click="$modal.hide(modalId);">Close</button>
                <button type="submit" class="btn btn-primary" @click="submit()" :disabled="formIsProcessing"><i class="fa fa-fw fa-save"></i>Save</button>
            </template>
        </modal>
    </div>
</template>

<script>
  import HasModal from "../../mixins/HasModal";

  export default {
    mixins: [HasModal],

    emits: ['created', 'updated'],

    /**
     * Input properties
     */
    props: {

      // Signature id
      signatureId: {
        type: Number,
        default: 0
      },

      // Initial signature data.
      initialSignature: {
        type: Object,
        default: () => {return {}}
      },

      // Modal dialog id
      modalId: {
        type: String,
        default: "signatureFormModal"
      },

      // Modal dialog title
      title: {
        type: String,
        default: "Add signature"
      },

      // Signature list component reference.
      reloadSignatureListRef: {
        type: String,
        default: 'signature_list'
      },
    },

    /**
     * Variables (models)
     */
    data() {
      return {
        // Current signature object
        signature: {},

        // Form fields
        fields: {},

        // Form validation error messages
        errors: {},

        // Internal validation state
        isValid: true,

        // Common error message
        error: '',

        // Form is processing now indicator.
        formIsProcessing: false,
      }
    },

    /**
     * Computed values
     */
    computed: {
      // Is form id "edit" mode
      isEdit() {
        return this.signatureId !== 0;
      },
    },

    /**
     * Functions (methods)
     */
    methods: {
      /**
       * Init form
       */
      init() {
        this.clearForm();
        this.signature = _.cloneDeep(this.initialSignature);
        this.fields = Object.assign({}, this.signature);
      },

      /**
       * Submit form data
       */
      submit() {
        this.validate();
        if (!this.isValid) return;
        if (this.isEdit) {
          this.updateSignature();
        } else {
          this.storeSignature();
        }
      },

      /**
       * Create signature
       */
      storeSignature() {
        let formData = this.formData();
        this.formIsProcessing = true;
        axios.post('/admin/system-utilities/email-signature/', formData)
          .then(response => {
            this.hideModal();
            this.$root.$refs[this.reloadSignatureListRef].loadSignatures();
            this.$emit('created', response.data);
            this.init();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to save data')
          })
          .finally(() => {
            this.formIsProcessing = false;
          });
      },

      /**
       * Save signature
       */
      updateSignature() {
        let formData = this.formData();
        formData.append('_method', "PATCH");
        this.formIsProcessing = true;
        axios.post('/admin/system-utilities/email-signature/' + this.signatureId, formData)
          .then(response => {
            this.hideModal();
            this.$root.$refs[this.reloadSignatureListRef].loadSignatures();
            this.$emit('updated', response.data);
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to save data')
          })
          .finally(() => {
            this.formIsProcessing = false;
          });
      },

      /**
       * Prepare form data
       */
      formData() {
        const formData = new FormData();
        Object.keys(this.fields).forEach(fieldName => {
          const value = this.fields[fieldName] ? this.fields[fieldName] : '';
          formData.append(fieldName, value);
        });

        return formData;
      },

      /**
       * Validate form
       */
      validate() {
        this.errors = {};
        this.isValid = true;

        // Validate email and signature.
        if (!this.fields.email) {
          this.setError('email', 'Email must be specified');
        }
        if (!this.fields.signature) {
          this.setError('signature', 'Signature must be specified');
        }
      },

      /**
       * Set validation error
       */
      setError(field, message) {
        if (!this.errors[field]) this.errors[field] = [];
        this.errors[field].push(message);
        this.isValid = false;
      },

      /**
       * Handle axios errors
       *
       * @param errorObj
       * @param defaultMessage
       */
      handleErrors(errorObj, defaultMessage) {
        if (errorObj.response.status === 422) {
          this.errors = errorObj.response.data.errors || {};
        } else {
          if (errorObj.response.status === 403) {
            this.error = defaultMessage + ': access denied.';
          } else {
            this.error = defaultMessage;
          }
        }
      },

      /**
       * Clear form fields and messages
       */
      clearForm() {
        this.fields = {};
        this.errors = {};
        this.error = '';
      }
    },

    /**
     * Initialize component on mount.
     */
    mounted() {
      this.init();
    },

  }
</script>
